import React from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import Accordion from '../organisms/accordion/accordion';
import CodeSkeleton from '../atoms/code/code.skeleton';
import ContactList from '../molecules/contact-list/contactList';
import FullscreenMedia from '../molecules/fullscreen-media/fullscreenMedia';
import ImageText from '../molecules/image-text/imageText';
import Infobox from '../molecules/infobox/infobox';
import MixedCopy from '../molecules/mixed-copy/mixedCopy';
import Quote from '../molecules/quote/quote';
import LoginMask from '../organisms/login-mask/loginMask.withRedux';
import Iframe from '../atoms/Iframe/Iframe';
import SocialPost from '../molecules/social-post/socialPost.withRedux';
import QuoteSlider from '../organisms/quote-slider/quote-slider';
import GameTeaser from '../organisms/game-teaser/gameTeaser';
import SplineScene from '../organisms/spline-scene/splineScene';

import Stakeholder from '../organisms/stakeholder/stakeholder';
import MapSkeleton from '../organisms/map/map.skeleton';
import SliderSkeleton from '../organisms/slider/slider.skeleton';
import EventSliderSkeleton from '../organisms/event-slider/eventSlider.skeleton';

const Code = dynamic({
  loader: () => import('../atoms/code/code'),
  loading: () => <CodeSkeleton />
});
const Download = dynamic(() => import('../molecules/download/download'));
const NewsletterForm = dynamic(() => import('../organisms/newsletter-form/newsletterForm'));
const BookingForm = dynamic(() => import('../organisms/booking-form/bookingForm'));
const PressForm = dynamic(() => import('../organisms/press-form/pressForm'));
const ContactForm = dynamic(() => import('../organisms/contact-form/contactForm'));
const InfoGraphicGroup = dynamic(() => import('../organisms/info-graphic-group/infoGraphicGroup'));
const InfoGraphic = dynamic(() => import('../molecules/info-graphic/infoGraphic'));
const LogoWall = dynamic(() => import('../organisms/logo-wall/logoWall'));
const EventSlider = dynamic({
  loader: () => import('../organisms/event-slider/eventSlider'),
  loading: () => <EventSliderSkeleton />,
  ssr: false
});
const Slider = dynamic({
  loader: () => import('../organisms/slider/slider'),
  loading: () => <SliderSkeleton />,
  ssr: false
});
const Map = dynamic({
  loader: () => import('../organisms/map/map'),
  loading: () => <MapSkeleton />,
  ssr: false
});

const AudioPLayer = dynamic({
  loader: () => import('../molecules/audio-player/audioPlayer'),
  ssr: false
});

const PdfViewer = dynamic({
  loader: () => import('../organisms/pdf-viewer/pdfViewer'),
  ssr: false
});

const EventList = dynamic({
  loader: () => import('../organisms/event-list/eventList')
});

const EducationSteps = dynamic({
  loader: () => import('../organisms/education/steps/educationSteps')
});

const TeaserContainer = dynamic({
  loader: () => import('../organisms/teaser-container/teaserContainer')
});

const TeaserContainerShaped = dynamic({
  loader: () => import('../organisms/teaser-container-shaped/teaserContainerShaped')
});

const JobEmbed = ({ iframeUrl }) => (
  <Iframe title="Job Embed" file={iframeUrl} height={650} noRatio />
);

JobEmbed.propTypes = {
  iframeUrl: PropTypes.string.isRequired
};

const Twingle = ({ iframeUrl }) => (
  <Iframe
    title="Twingle"
    file={iframeUrl}
    mobileHeight={650}
    height={1050}
    noRatio
    allow="payment"
  />
);

const IframeEmbed = ({ iFrameTitle, iFrameUrl, height, mobileHeight }) => (
  <Iframe
    title={iFrameTitle}
    file={iFrameUrl}
    mobileHeight={mobileHeight}
    height={height}
    noRatio
    allow="payment; autoplay; fullscreen;"
  />
);

Twingle.propTypes = {
  iframeUrl: PropTypes.string.isRequired
};

IframeEmbed.propTypes = {
  iFrameTitle: PropTypes.string.isRequired,
  iFrameUrl: PropTypes.string.isRequired,
  height: PropTypes.number.isRequired,
  mobileHeight: PropTypes.number.isRequired
};

const getEmbed = block => ({
  copy: <MixedCopy html={block.html} />,
  download: <Download {...block.data} />,
  code: <Code {...block.data} />,
  infoBox: <Infobox {...block.data} />,
  logoWall: <LogoWall {...block.data} />,
  accordion: <Accordion {...block.data} />,
  contact: <ContactList {...block.data} />,
  quote: <Quote {...block.data} />,
  quoteSlider: <QuoteSlider {...block.data} />,
  embeddedVideo: <FullscreenMedia type="embeddedVideo" {...block.data} />,
  video: <FullscreenMedia type="video" {...block.data} />,
  vrImage: <FullscreenMedia type="vrImage" {...block.data} />,
  imageText: <ImageText {...block.data} />,
  fullwidthImage: <FullscreenMedia type="fullwidthImage" {...block.data} />,
  gallery: <Slider {...block.data} />,
  vrGallery: <Slider {...block.data} />,
  eventGallery: <EventSlider {...block.data} />,
  singleInfographic: <InfoGraphic {...block.data} />,
  infographic: <InfoGraphicGroup {...block.data} />,
  newsletterForm: <NewsletterForm />,
  bookingInquiryForm: <BookingForm />,
  pressDistributorForm: <PressForm />,
  contactForm: <ContactForm />,
  jobs: <JobEmbed {...block.data} />,
  map: <Map {...block.data} />,
  audio: <AudioPLayer {...block.data} />,
  loginMask: <LoginMask {...block.data} />,
  socialPost: <SocialPost {...block.data} />,
  pdfviewer: <PdfViewer {...block.data} />,
  eventsList: <EventList {...block.data} />,
  steps: <EducationSteps {...block.data} />,
  automaticTeasers: <TeaserContainer {...block.data} />,
  teaserContainer: <TeaserContainerShaped data={block.data} />,
  stakeholderLogoSlider: <Stakeholder {...block.data} hasBackground />,
  twingle: <Twingle {...block.data} />,
  iframe: <IframeEmbed {...block.data} />,
  gameTeaser: <GameTeaser {...block.data} />,
  splineScene: <SplineScene {...block.data} />
});

export default getEmbed;
